import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { works, categories, type Work } from '@/mocks/works';
import { articles, type Article } from '@/mocks/articles';

function getCategoryName(categoryId: string): string {
  if (categoryId === 'news') return '最新动态';
  const cat = categories.find((c) => c.id === categoryId);
  return cat ? cat.name : '全部作品';
}

export default function CategoryPage() {
  const { categoryId = 'all' } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(categoryId);

  useEffect(() => {
    setActiveTab(categoryId);
  }, [categoryId]);

  const isNewsPage = categoryId === 'news';
  const categoryName = getCategoryName(categoryId);
  const pageTitle = isNewsPage
    ? '佐佐木明希最新动态 - 新闻资讯、专题报道、活动信息'
    : `佐佐木明希${categoryName} - 作品全览`;
  const pageDesc = isNewsPage
    ? '佐佐木明希最新动态频道，涵盖新闻资讯、专题报道、活动信息、访谈实录等内容，第一时间掌握佐佐木明希的全部动态消息。'
    : `浏览佐佐木明希${categoryName}的全部作品，包含作品名称、发布日期、制作公司、时长等详细信息。`;

  const filteredWorks = categoryId === 'all' ? works : works.filter((w) => w.category === categoryId);

  const tabItems = isNewsPage
    ? [
        { id: 'news', label: '全部动态' },
        { id: 'news-interview', label: '访谈' },
        { id: 'news-report', label: '专题' },
        { id: 'news-event', label: '活动' },
      ]
    : categories.map((c) => ({
        id: c.id,
        label: c.name,
      }));

  const [newsFilter, setNewsFilter] = useState('all');
  const filteredArticles = isNewsPage
    ? newsFilter === 'all'
      ? articles
      : articles.filter((a) => {
          if (newsFilter === 'news-interview') return a.category === '访谈';
          if (newsFilter === 'news-report') return a.category === '专题';
          if (newsFilter === 'news-event') return a.category === '活动';
          return true;
        })
    : [];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`佐佐木明希,${categoryName},佐佐木明希作品,佐佐木明希动态`}
        canonicalUrl={`https://akiupdate.github.io/category/${categoryId}`}
      />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: isNewsPage ? '最新动态' : categoryId === 'all' ? '全部作品' : categoryName },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {isNewsPage ? '最新动态' : `${categoryName}`}
            </h2>
            <p className="text-foreground-500 text-sm">
              {isNewsPage
                ? '第一时间掌握佐佐木明希的最新资讯、活动与专题报道'
                : `共 ${filteredWorks.length} 部作品`}
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-8 overflow-x-auto">
            <div className="inline-flex bg-background-100 rounded-full p-1 gap-0.5">
              {tabItems.map((tab) => {
                const isNewsTab = isNewsPage && tab.id.startsWith('news-');
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (isNewsTab) {
                        setNewsFilter(tab.id);
                      } else if (tab.id === 'news') {
                        setNewsFilter('all');
                      } else {
                        navigate(`/category/${tab.id}`);
                      }
                    }}
                    className={`px-5 py-2 rounded-full text-sm font-label whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      (isNewsPage && tab.id === 'news' && newsFilter === 'all') ||
                      (isNewsPage && isNewsTab && newsFilter === tab.id) ||
                      (!isNewsPage && activeTab === tab.id)
                        ? 'bg-background-50 text-foreground-950 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* News Content */}
          {isNewsPage && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`佐佐木明希动态 - ${article.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-800 text-xs font-label backdrop-blur-sm">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 font-label mb-2">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Works Grid */}
          {!isNewsPage && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredWorks.map((work) => (
                <Link
                  key={work.id}
                  to={`/article/${work.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={work.coverImage}
                      alt={work.title}
                      title={`佐佐木明希作品 - ${work.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                      {work.title}
                    </h3>
                    <p className="text-xs text-foreground-500 font-label mb-2">{work.publisher}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400 font-label">
                      <span>{work.releaseDate}</span>
                      <span>·</span>
                      <span>{work.duration}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!isNewsPage && filteredWorks.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-file-list-3-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无作品</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}